import config from '../config';
import { createSuburbLandingPath } from './suburbLandingPages';
import { createStateLandingPath, stateLandingPages } from './stateLandingPages';
import {
  createSuburbTrailerTypePath,
  trailerTypeSlugConfig,
} from './suburbTrailerTypeLandingPages';
import { getNationalTrailerTypeLandingPage } from './nationalTrailerTypeLandingPages';
import { createTrailerTypePagePath } from './suburbLandingPages';

const absoluteUrl = path => `${config.canonicalRootURL}${path}`;

/** Short crumb labels for location × type pages (docs/trailer_hire_breadcrumbs.md). */
export const trailerTypeBreadcrumbLabels = {
  'cage-trailers': 'Cage trailers',
  'box-trailers': 'Box trailers',
  'enclosed-trailers': 'Enclosed trailers',
  'car-trailers': 'Car trailers',
};

/**
 * Resolve a suburb landing's `state` label to a live state landing page (allowlisted only).
 */
export const getStateLandingPageForSuburb = suburb => {
  if (!suburb?.state) {
    return null;
  }
  return (
    Object.values(stateLandingPages).find(
      page => page.name === suburb.state || page.breadcrumbLabel === suburb.state
    ) || null
  );
};

const breadcrumbListSchema = items => {
  if (!items || items.length === 0) {
    return null;
  }

  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: absoluteUrl(item.path),
    })),
  };
};

/**
 * UI + schema crumb items for a suburb/location landing page.
 * Pattern: Home › Trailer Hire › [{State} when live] › {Location}
 * State crumb is only included when that state landing page exists.
 */
export const buildSuburbLandingBreadcrumbItems = suburb => {
  if (!suburb?.slug || !suburb?.name) {
    return [];
  }

  const items = [
    {
      id: 'home',
      label: 'Home',
      path: '/',
      current: false,
      link: { name: 'LandingPage' },
    },
    {
      id: 'trailer-hire',
      label: 'Trailer Hire',
      path: '/trailer-hire',
      current: false,
      link: { name: 'TrailerHirePage' },
    },
  ];

  const statePage = getStateLandingPageForSuburb(suburb);
  if (statePage) {
    items.push({
      id: 'state',
      label: statePage.breadcrumbLabel || statePage.name,
      path: createStateLandingPath(statePage.slug),
      current: false,
      link: { name: 'StateLandingPage', params: { stateSlug: statePage.slug } },
    });
  }

  items.push({
    id: 'location',
    label: suburb.name,
    path: createSuburbLandingPath(suburb.slug),
    current: true,
    link: null,
  });

  return items;
};

export const createSuburbLandingBreadcrumbSchema = suburb => {
  return breadcrumbListSchema(buildSuburbLandingBreadcrumbItems(suburb));
};

/**
 * UI + schema crumb items for a location × type landing page.
 * Pattern: Home › Trailer Hire › [{State} when live] › {Location} › {Type}
 */
export const buildSuburbTrailerTypeBreadcrumbItems = (suburb, typeSlug) => {
  if (!suburb?.slug || !suburb?.name || !typeSlug || !trailerTypeSlugConfig[typeSlug]) {
    return [];
  }

  const typeLabel = trailerTypeBreadcrumbLabels[typeSlug];
  if (!typeLabel) {
    return [];
  }

  const locationItems = buildSuburbLandingBreadcrumbItems(suburb);
  if (locationItems.length === 0) {
    return [];
  }

  // Location is no longer current — link back to the suburb landing.
  const items = locationItems.map(item =>
    item.id === 'location'
      ? {
          ...item,
          current: false,
          link: { name: 'SuburbLandingPage', params: { slug: suburb.slug } },
        }
      : item
  );

  items.push({
    id: 'type',
    label: typeLabel,
    path: createSuburbTrailerTypePath(suburb.slug, typeSlug),
    current: true,
    link: null,
  });

  return items;
};

export const createSuburbTrailerTypeBreadcrumbSchema = (suburb, typeSlug) => {
  return breadcrumbListSchema(buildSuburbTrailerTypeBreadcrumbItems(suburb, typeSlug));
};

/**
 * UI + schema crumb items for a national trailer-type landing page.
 * Pattern: Home › Trailer Hire › {Type}
 */
export const buildNationalTrailerTypeBreadcrumbItems = typeSlug => {
  const page = getNationalTrailerTypeLandingPage(typeSlug);
  if (!page) {
    return [];
  }

  return [
    {
      id: 'home',
      label: 'Home',
      path: '/',
      current: false,
      link: { name: 'LandingPage' },
    },
    {
      id: 'trailer-hire',
      label: 'Trailer Hire',
      path: '/trailer-hire',
      current: false,
      link: { name: 'TrailerHirePage' },
    },
    {
      id: 'type',
      label: page.breadcrumbLabel,
      path: createTrailerTypePagePath(page.slug),
      current: true,
      link: null,
    },
  ];
};

export const createNationalTrailerTypeBreadcrumbSchema = typeSlug => {
  return breadcrumbListSchema(buildNationalTrailerTypeBreadcrumbItems(typeSlug));
};
